import { addSentryBreadcrumb } from "./sentry";

/**
 * Wraps global fetch to emit Sentry breadcrumbs for every outgoing request.
 * Captures Supabase REST calls and any other fetch-based API traffic that
 * does not go through React Query (e.g. direct supabase.from chains).
 *
 * Call once after Sentry init. Safe to call multiple times — idempotent.
 */
let patched = false;

export function instrumentFetch() {
  if (patched) return;
  if (typeof window === "undefined" || !window.fetch) return;

  const originalFetch = window.fetch;

  const instrumentedFetch = async function instrumentedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const method = init?.method?.toUpperCase() ?? "GET";

    try {
      addSentryBreadcrumb({
        category: "api",
        type: "http",
        message: `fetch ${method} ${url}`,
        data: { url, method },
        level: "info",
      });
    } catch {
      /* breadcrumb errors must never break fetch */
    }

    try {
      const response = await originalFetch.call(window, input, init);
      try {
        addSentryBreadcrumb({
          category: "api",
          type: "http",
          message: `fetch ${method} ${url} — ${response.status}`,
          data: { url, method, status: response.status, statusText: response.statusText },
          level: response.ok ? "info" : "warning",
        });
      } catch {
        /* breadcrumb errors must never break fetch */
      }
      return response;
    } catch (error) {
      try {
        addSentryBreadcrumb({
          category: "api",
          type: "http",
          message: `fetch ${method} ${url} — network error`,
          data: { url, method, error: String(error) },
          level: "error",
        });
      } catch {
        /* breadcrumb errors must never break fetch */
      }
      throw error;
    }
  };

  try {
    // Install the wrapper and mark patched. If the assignment or
    // bookkeeping throws (e.g. window.fetch defined as non-writable),
    // leave the original native fetch intact and skip patching.
    window.fetch = instrumentedFetch;
    patched = true;
  } catch {
    /* never let installation failures break native fetch */
  }
}
